import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	AttentionTypes,
	MatterFileType,
	CondoCorp,
	CondoManagement,
} from '@models/matter';
import { Store } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { CondoCorporationAttentionContactModalComponent } from './condo-corporation-attention-modal/condo-corporation-attention-contact-modal.component';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { EregFileViewerComponent } from '../../closing/components/ereg-file-viewer/ereg-file-viewer.component';
import { map } from 'rxjs/operators';
import { deleteField } from '@angular/fire/firestore';
import { combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Employee } from '@models/company';

@Component({
	selector: 'app-property-condo-corp',
	templateUrl: './condo-corp.component.html',
	styleUrls: ['./condo-corp.component.scss'],
	animations: [openClose(300)],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class PropertyCondoCorpComponent {
	public AI_GENERATED_ICON = AI_GENERATED_ICON;
	public attentionTypeAiTooltip$ = this.store
		.select(SelectedMatter.getFieldMappings)
		.pipe(
			map((fieldMappings) =>
				fieldMappings?.condoCorporation?.attention?.attentionType
					? AI_GENERATED_ICON.tooltip
					: null,
			),
		);
	public isRefinanceOrPurchase$ = this.store
		.select(SelectedMatter.matterFileType)
		.pipe(
			map((fileType) =>
				[MatterFileType.REFINANCE, MatterFileType.PURCHASE].includes(fileType),
			),
		);
	public condoCorporation$ = this.store.select(SelectedMatter.condoCorporation);
	public attentionContact$ = this.store
		.select(SelectedMatter.attentionContact)
		.pipe(
			tap((contact) => {
				this.attentionContact = contact ?? null;
			}),
		);
	public statusCertificate$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestStatusCertificate,
	);
	public matterId$ = this.store.select(SelectedMatter.id);
	public departmentName$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.attention?.departmentName),
	);
	public attentionType$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.attention?.attentionType),
	);

	public managementType$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.managementType),
	);
	public baseFee$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.baseFee ?? 0),
	);
	public urgencyFee$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.urgencyFees ?? 0),
	);

	public totalFee$ = combineLatest([this.baseFee$, this.urgencyFee$]).pipe(
		map(([baseFee, urgencyFee]) => baseFee + urgencyFee),
	);

	public condoCorp$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.condoCorp),
		tap((condoCorp) => {
			this.condoCorp = condoCorp;
		}),
	);
	public management$ = this.condoCorporation$.pipe(
		map((condoCorporation) => condoCorporation?.management),
		tap((management) => {
			this.management = management;
		}),
	);

	private condoCorp: CondoCorp;
	private management: CondoManagement;
	public ICON = ICON;
	public attentionOptions = [
		{ label: AttentionTypes.DEPARTMENT, value: AttentionTypes.DEPARTMENT },
		{ label: AttentionTypes.PERSON.toString(), value: AttentionTypes.PERSON },
	];
	public MatterDocumentType = MatterDocumentType;
	public MatterFileType = MatterFileType;
	private attentionContact: Employee = null;

	constructor(
		private matterDispatch: MatterDispatch,
		private matDialog: MatDialog,
		private drawerService: DrawerService,
		private store: Store,
	) {}

	setCondoCorporationNumber(value: string) {
		this.matterDispatch.OverwriteFields('condoCorporationNumber', value);
	}

	onCreateCondoCorporationAttentionContact() {
		this.matDialog.open(CondoCorporationAttentionContactModalComponent, {
			data: {
				company: this.management ?? this.condoCorp,
				checkboxLabel: this.management
					? 'Same as management company'
					: 'Same as condo corporation',
			},
		});
	}

	selectAttentionOption(option: AttentionTypes) {
		if (option === AttentionTypes.PERSON && !this.attentionContact) {
			this.onCreateCondoCorporationAttentionContact();
		}
		this.matterDispatch.OverwriteFields(
			'condoCorporation.attention.attentionType',
			option,
		);
	}

	setDepartmentName(name: string) {
		this.matterDispatch.OverwriteFields(
			'condoCorporation.attention.departmentName',
			name,
		);
	}

	setBaseFee(fee: number) {
		this.matterDispatch.OverwriteFields('condoCorporation.baseFee', fee);
	}

	setUrgencyFee(fee: number) {
		this.matterDispatch.OverwriteFields('condoCorporation.urgencyFees', fee);
	}

	contactDropdownOptionSelect(event: DropdownResult<{ name: string }>) {
		switch (event.result.name) {
			case 'Edit':
				this.matDialog.open(CondoCorporationAttentionContactModalComponent, {
					data: {
						employee: this.attentionContact,
						company: this.management ?? this.condoCorp,
						checkboxLabel: this.management
							? 'Same as management company'
							: 'Same as condo corporation',
					},
				});
				break;
			case 'Remove':
				this.matterDispatch.OverwriteFields(
					'condoCorporation.attention.person',
					deleteField(),
				);
				break;
		}
	}

	viewStatusCertificate(file: MatterFile) {
		this.drawerService.openDrawer(EregFileViewerComponent, {
			title: file.name,
			data: { file },
		});
	}
}
